import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { GaragePendingApprovalsResponse } from '../types';
import { OfferStatus, QUEUE_STATUSES } from '../types/offers';
import { useAuth } from '../hooks/useAuth';
import OffersQueueTab from '../components/offers/OffersQueueTab';
import PackagesQueueTab from '../components/packages/PackagesQueueTab';
import { PACKAGE_QUEUE_STATUSES, PackageStatus } from '../types/packages';
import { ToastView, useToast } from '../components/promotions/useToast';

type ApprovalsTab = 'services' | 'offers' | 'packages';

const TABS: readonly ApprovalsTab[] = ['services', 'offers', 'packages'];

function ServicesTab({ token }: { token: string | null }) {
  const [approvals, setApprovals] = useState<GaragePendingApprovalsResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    api.listPendingApprovals(token)
      .then(setApprovals)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <>
      {error && <div className="admin-info-banner danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : approvals.length === 0 ? (
          <div className="admin-empty-state">
            <p>No garages with pending service approvals</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Garage Name</th>
                  <th>Awaiting review</th>
                  <th>Most Recent Pending Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((g) => (
                  <tr key={g.garageId}>
                    <td style={{ fontWeight: 500 }}>{g.garageName}</td>
                    <td>
                      {g.pendingCount > 0 && (
                        <span className="admin-badge pending" style={{ marginRight: '0.5rem' }}>
                          {g.pendingCount} new service{g.pendingCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {(g.pendingVariantCount ?? 0) > 0 && (
                        <span className="admin-badge pending">
                          {g.pendingVariantCount} price change{g.pendingVariantCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td>{new Date(g.mostRecentPendingDate).toLocaleDateString()}</td>
                    <td>
                      <Link to={`/garages/${g.garageId}/services?filter=pending`} className="admin-link">
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default function ServiceApprovalsPage() {
  const auth = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Spec 062 / 081b — the tab and the queue status filter live in the URL so links and Back work.
  const tabParam = searchParams.get('tab') as ApprovalsTab | null;
  const tab: ApprovalsTab = tabParam && TABS.includes(tabParam) ? tabParam : 'services';
  const statusParam = searchParams.get('status');
  const offerStatus: OfferStatus = statusParam && QUEUE_STATUSES.includes(statusParam as OfferStatus)
    ? statusParam as OfferStatus : 'PENDING_APPROVAL';
  const packageStatus: PackageStatus = statusParam && PACKAGE_QUEUE_STATUSES.includes(statusParam as PackageStatus)
    ? statusParam as PackageStatus : 'PENDING_APPROVAL';

  const initialToast = (location.state as { toast?: string } | null)?.toast;
  const { toast } = useToast(initialToast ? { message: initialToast, type: 'success' } : null);

  const [pendingOffers, setPendingOffers] = useState<number | null>(null);
  const [pendingPackages, setPendingPackages] = useState<number | null>(null);

  // Badge count for the Offers tab. Best effort: the Services tab must not depend on the offers API.
  useEffect(() => {
    if (!auth.token) return;
    let cancelled = false;
    api.listOfferQueue(auth.token, 'PENDING_APPROVAL', 0, 1)
      .then((res) => { if (!cancelled) setPendingOffers(res.totalElements); })
      .catch(() => { /* count stays hidden */ });
    return () => { cancelled = true; };
  }, [auth.token]);

  // Spec 081b — badge count for the Packages tab, best effort like the Offers one.
  useEffect(() => {
    if (!auth.token) return;
    let cancelled = false;
    api.listPackageQueue(auth.token, { status: 'PENDING_APPROVAL', size: 1 })
      .then((res) => { if (!cancelled) setPendingPackages(res.totalElements); })
      .catch(() => { /* count stays hidden */ });
    return () => { cancelled = true; };
  }, [auth.token]);

  const selectTab = (next: ApprovalsTab) => {
    setSearchParams(next === 'services' ? {} : { tab: next });
  };

  const selectStatus = useCallback((next: OfferStatus) => {
    setSearchParams(next === 'PENDING_APPROVAL' ? { tab: 'offers' } : { tab: 'offers', status: next });
  }, [setSearchParams]);

  const selectPackageStatus = useCallback((next: PackageStatus) => {
    setSearchParams(next === 'PENDING_APPROVAL' ? { tab: 'packages' } : { tab: 'packages', status: next });
  }, [setSearchParams]);

  return (
    <div>
      <ToastView toast={toast} />
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', marginBottom: '1.25rem' }}>
        Service Approvals
      </h2>

      <div className="workspace-tabs approvals-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'services'}
          className={`workspace-tab${tab === 'services' ? ' workspace-tab--active' : ''}`}
          onClick={() => selectTab('services')}
        >
          Services
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'offers'}
          className={`workspace-tab${tab === 'offers' ? ' workspace-tab--active' : ''}`}
          onClick={() => selectTab('offers')}
        >
          Offers{pendingOffers !== null ? ` (${pendingOffers})` : ''}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'packages'}
          className={`workspace-tab${tab === 'packages' ? ' workspace-tab--active' : ''}`}
          onClick={() => selectTab('packages')}
        >
          Packages{pendingPackages !== null ? ` (${pendingPackages})` : ''}
        </button>
      </div>

      <div className="workspace-tab-content">
        {tab === 'services' ? (
          <ServicesTab token={auth.token} />
        ) : !auth.token ? null : tab === 'packages' ? (
          <PackagesQueueTab
            key={packageStatus}
            token={auth.token}
            status={packageStatus}
            onStatusChange={selectPackageStatus}
            onPendingCount={setPendingPackages}
          />
        ) : (
          <OffersQueueTab
            key={offerStatus}
            token={auth.token}
            status={offerStatus}
            onStatusChange={selectStatus}
            onPendingCount={setPendingOffers}
          />
        )}
      </div>
    </div>
  );
}
